import React from "react";
import { formatBoldText } from "../../utils/textFormatting";

/**
 * Helper function to format phase name for display
 */
const formatPhaseName = (phase) => {
  if (!phase) return null;
  const phaseMap = {
    'stock_options': '📊 Stock Options',
    'events': '📰 Events',
    'trading': '💼 Trading',
    'end_of_round': '🎲 End of Round'
  };
  return phaseMap[phase] || phase;
};

/**
 * GameLogPopup
 * Displays the entire game log in a popup format.
 * When an entry has playerName, background and border use that player's color.
 */
const GameLogPopup = ({ updates = [], gameLog = [], playerColors = {} }) => {
  const totalEntries = gameLog.length + updates.length;
  const shouldScroll = totalEntries > 5;
  
  const entryHeight = 80;
  const gap = 12;
  const headerHeight = 60;
  const padding = 40;
  const maxContentHeight = (entryHeight * 5) + (gap * 4) + headerHeight + padding;

  const getEntryStyle = (entry) => {
    const isPlayerAction = entry.playerName && playerColors[entry.playerName];
    const playerColor = isPlayerAction ? playerColors[entry.playerName] : null;
    const base = {
      padding: '12px 16px',
      borderRadius: '4px',
      fontSize: '0.95rem',
      lineHeight: '1.5'
    };
    if (playerColor) {
      return {
        ...base,
        background: `${playerColor}15`,
        borderLeft: `4px solid ${playerColor}`
      };
    }
    return {
      ...base,
      background: 'rgba(33, 150, 243, 0.05)',
      borderLeft: '4px solid rgba(33, 150, 243, 0.5)'
    };
  };

  const getHeaderColor = (entry) => {
    if (entry.playerName && playerColors[entry.playerName]) {
      return playerColors[entry.playerName];
    }
    return 'rgba(33, 150, 243, 0.9)';
  };
  
  return (
    <div style={{
      width: '100%',
      maxHeight: shouldScroll ? `${maxContentHeight}px` : 'none',
      overflowY: shouldScroll ? 'auto' : 'visible',
      padding: '20px 24px',
      boxSizing: 'border-box'
    }}>

      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {/* Market News from backend */}
        {gameLog.length > 0 &&
          gameLog
            .slice()
            .reverse()
            .map((entry, index) => {
              const phaseLabel = formatPhaseName(entry.phase);
              const entryStyle = getEntryStyle(entry);
              const headerColor = getHeaderColor(entry);
              return (
                <div 
                  key={`log-${index}`} 
                  style={entryStyle}
                >
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: headerColor,
                    }}>
                      R{entry.round}
                    </span>
                    {phaseLabel && (
                      <span style={{ 
                        fontSize: '0.85rem',
                        color: entry.playerName && playerColors[entry.playerName] ? `${playerColors[entry.playerName]}cc` : 'rgba(33, 150, 243, 0.7)',
                        fontWeight: 500
                      }}>
                        {phaseLabel}
                      </span>
                    )}
                  </div>
                  <span style={{ color: '#333' }}>
                    {formatBoldText(entry.message)}
                  </span>
                </div>
              );
            })}

        {/* Player updates */}
        {updates.length > 0 &&
          updates
            .slice()
            .reverse()
            .map((update, index) => (
              <div 
                key={`update-${index}`} 
                style={{
                  padding: '12px 16px',
                  background: 'rgba(156, 39, 176, 0.05)',
                  borderLeft: '4px solid rgba(156, 39, 176, 0.5)',
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  lineHeight: '1.5'
                }}
              >
                <span style={{ 
                  marginRight: '8px',
                  fontSize: '1rem'
                }}>
                  🎴
                </span>
                <span style={{ color: '#333' }}>
                  {update}
                </span>
              </div>
            ))}

        {gameLog.length === 0 && updates.length === 0 && (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: '#999',
            fontSize: '0.95rem'
          }}>
            No game log entries yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default GameLogPopup;

